/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { SEED_MIX_TYPE, SeedMix } from '../types/SeedOrderFormType';
import { TableContainer, Stack, Typography, Table, TableBody, useTheme, Box } from '@mui/material';
import {
  allSeedsCount,
  allSeedWeightForPaddock,
  roundedTo,
  totalPriceForPaddock,
} from '../utils/calculateTotal';
import { QuantityChip } from '../../dashboard/components/QuantityChip';
import { SeedOrderTableProductRow } from './SeedOrderTableProductRow';
import TestIds from '../../../testIds';
import { SeedStatsLabel } from './SeedStatsLabel';
import { formatPrice } from '../../../util/helpers';

const SeedOrderTableMobile = ({ seed }: { seed: SeedMix }) => {
  const theme = useTheme();
  return (
    <TableContainer data-testid={TestIds.feat.agreement.salesOrder.table}>
      {seed.paddocks.map((paddock) => {
        const weight = allSeedWeightForPaddock(seed.products, paddock);
        const sowingRate = weight / paddock.hectares;
        const pricePerHa = totalPriceForPaddock(seed.products, paddock);

        return (
          <Box key={`${seed.id}-${paddock.name}`} display='flex' flexDirection={'column'}>
            <Stack direction='column' spacing={1} py={3} px={2}>
              <Typography variant='h6' color={theme.colors.primary500} fontWeight='bold'>
                {paddock.name}
              </Typography>
              <Box flexDirection='column' display='flex' flexWrap='wrap' gap='8px'>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <QuantityChip label={`${paddock.hectares} Ha`} />
                  <QuantityChip label={`${allSeedsCount(seed.products)} products`} />
                  {seed.mixType === SEED_MIX_TYPE.PREMIX ? null : (
                    <QuantityChip label={`${roundedTo(weight)}kg`} />
                  )}
                </Stack>

                <Stack direction='row' alignItems='center' spacing={2}>
                  {seed.mixType === SEED_MIX_TYPE.PREMIX ? null : (
                    <SeedStatsLabel title='Sowing Rate' value={`${roundedTo(sowingRate)} kg/Ha`} />
                  )}
                  <SeedStatsLabel title='Price per ha' value={formatPrice(pricePerHa)} />
                </Stack>
              </Box>
            </Stack>

            <Table aria-label='Seed orders table'>
              <TableBody>
                {seed.products.map((seedProduct) => (
                  <SeedOrderTableProductRow
                    key={`${seedProduct.lineId}=${paddock.name}`}
                    paddock={paddock}
                    seedProduct={seedProduct}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      })}
    </TableContainer>
  );
};

export default SeedOrderTableMobile;
